import {
  Button,
  Flex,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Skeleton,
  Text,
} from '@chakra-ui/react';
import {colors, useAddressContext} from 'components/providers';
import {POPOVER_REGIONS} from 'config';
import {useLocalStorageState, useTranslation} from 'hooks';
import {useRouter} from 'next/router';
import {FC, useRef, useState} from 'react';
import {LanguageEnum, StorageKeyEnum} from 'types';
import NextImageUI from 'ui/NextImage';

export const SelectAddress: FC = () => {
  const {selectedAddress, isAddressesLoading, openAddAddressModal} = useAddressContext();
  const {t} = useTranslation();
  const {locale, reload} = useRouter();
  const [isOpen, setIsOpen] = useState(true);
  const closePopover = () => setIsOpen(false);
  const buttonRef = useRef(null);
  const [region, setRegion] = useLocalStorageState(StorageKeyEnum.REGION_COORDS);
  const [, setRegionName] = useLocalStorageState('region_name');

  const fieldText = selectedAddress
    ? `${selectedAddress.address.street} ${selectedAddress.address.number}`
    : t('SELECT_ADDRESS');

  const handleClick = () => {
    openAddAddressModal(selectedAddress?.id);
  };

  if (isAddressesLoading) {
    return <Skeleton maxW="360px" w={['100%', '360px']} h="45px" borderRadius="16px" />;
  }

  const renderSelectBtn = () => {
    return (
      <Button
        ref={buttonRef}
        w={['100%', '360px']}
        leftIcon={
          <Flex ml={['8px', '15px']}>
            <NextImageUI width={35} height={35} src="/profile-menu/location-icon.svg" alt="Location" />
          </Flex>
        }
        rightIcon={
          <Flex mr={['8px', '15px']}>
            <NextImageUI width={24} height={24} src="/slider/arrow-icon.svg" alt="Arrow" />
          </Flex>
        }
        h="45px"
        bg={colors.lightGray}
        p={0}
        onClick={handleClick}
        _focus={{border: 'none'}}
      >
        <Text
          fontSize="16px"
          fontWeight="600"
          textAlign="left"
          w="90%"
          overflow="hidden"
          as="span"
          mt="1px"
          textOverflow="ellipsis"
        >
          {fieldText}
        </Text>
      </Button>
    );
  };

  return (
    <>
      {!selectedAddress && !region ? (
        <Popover
          isOpen={isOpen}
          onClose={async () => {
            setRegionName('closed');
            setRegion(`${POPOVER_REGIONS[0].coordinates.latitude},${POPOVER_REGIONS[0].coordinates.longitude}`);
            closePopover();
          }}
          closeOnBlur={false}
          initialFocusRef={buttonRef}
          closeOnEsc={false}
        >
          <PopoverTrigger>{renderSelectBtn()}</PopoverTrigger>
          <PopoverContent
            aria-label="Region Content"
            boxShadow="lg"
            borderRadius="md"
            p={5}
            _focus={{boxShadow: 'none'}}
          >
            <PopoverArrow />
            <PopoverCloseButton />
            <Text fontWeight="normal" aria-label="Region Title">
              <Text as="span" fontWeight="bold">
                {t('selectRegionDelivery')}
              </Text>
              {t('selectRegion')}
            </Text>

            <PopoverBody>
              <Flex direction="column" gap={2} mt={2}>
                {POPOVER_REGIONS.map((region) => (
                  <Button
                    key={region.id}
                    size="xs"
                    variant="outline"
                    onClick={async () => {
                      setRegion(`${region.coordinates.latitude},${region.coordinates.longitude}`);
                      setRegionName(region.translations.en);
                      if (['/', '/en', '/ru', '/ro'].includes(window.location.pathname)) {
                        reload();
                      }
                    }}
                  >
                    {region.translations[`${(locale as LanguageEnum) ?? 'ro'}`]}
                  </Button>
                ))}
              </Flex>
            </PopoverBody>
          </PopoverContent>
        </Popover>
      ) : (
        renderSelectBtn()
      )}
    </>
  );
};
